import type { Preference } from "../queries/company.queries";

type ChargePreferences = Pick<Preference, "vat" | "esf">;

// VAT and ESF are either/or, enforced in company preferences (see
// company-setting.page.tsx's mutuallyExclusiveFieldName toggle). This is the
// shared "is either charge active" gate used to default a new billed item's
// vatable checkbox to checked.
export function isChargeModeActive(
	preferenceDetails?: Partial<ChargePreferences> | null,
): boolean {
	return Boolean(preferenceDetails?.vat) || (preferenceDetails?.esf ?? 0) > 0;
}

// ESF is only the active mode when VAT is off — `esf` (the rate) is left
// at its fixed default even while VAT is on, so `esf > 0` alone isn't
// enough to tell whether ESF mode itself is active.
export function isEsfModeActive(
	preferenceDetails?: Partial<ChargePreferences> | null,
): boolean {
	return (
		!preferenceDetails?.vat && (preferenceDetails?.esf ?? 0) > 0
	);
}

export interface ChargeAmounts {
	taxable_sales_vat: number;
	vat: number;
	amount_for_esf: number;
	esf: number;
}

// `chargeableBase` is the gross total of whichever items are marked
// `vatable` — the same bucket either way, just routed into a different pair
// of output fields depending on which mode is active. Mirrors
// calculateWithTax in invoice-form.component.jsx.
export function computeChargeAmounts(
	chargeableBase: number,
	preferenceDetails?: Partial<ChargePreferences> | null,
): ChargeAmounts {
	const vatModeActive = Boolean(preferenceDetails?.vat);
	const esfModeActive = isEsfModeActive(preferenceDetails);

	if (vatModeActive) {
		return {
			taxable_sales_vat: chargeableBase,
			vat: chargeableBase > 0 ? 0.13 * chargeableBase : 0,
			amount_for_esf: 0,
			esf: 0,
		};
	}

	if (esfModeActive) {
		return {
			taxable_sales_vat: 0,
			vat: 0,
			amount_for_esf: chargeableBase,
			esf:
				chargeableBase > 0
					? chargeableBase * ((preferenceDetails?.esf ?? 0) / 100)
					: 0,
		};
	}

	return { taxable_sales_vat: 0, vat: 0, amount_for_esf: 0, esf: 0 };
}
